import { Divider, Flex, Group, Paper, Stack } from '@mantine/core';
import { UseQueryOptions } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Responsive, ResponsiveProps, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate, useParams } from 'react-router';
import { useInitiatives } from '../../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { usePageTemplates } from '../../../../api/pages-client/pages-client.hooks';
import type { Component } from '../../../../api/pages-client/pages-client.type';
import i18n from '../../../../base-dictionary';
import { useDocumentTitle } from '../../../../helpers/general-helpers';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { H2, Text } from '../../../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../../../menu-driven-container/menu-driven-container';
import { createDefaultLayout, layoutDataToLayout } from '../../dashboards.helpers';
import { DashboardInfoCard } from '../dashboard-info-card';
import { DashboardWidget } from '../dashboard-widget';
import {
  convertPageTemplateToComponents,
  extractTemplateObjectKeys,
  getTemplateObjectType,
  validateTemplateObjectMappings,
} from './dashboard-template-helpers';
import { type ObjectKeyInfo, ObjectType } from './dashboard-template.type';

const ResponsiveGridLayout = WidthProvider(Responsive) as React.ComponentType<ResponsiveProps>;

export const DashboardTemplate = () => {
  useDocumentTitle('Dashboard Template - Bloomfilter');
  const { templateId } = useParams();
  const navigate = useNavigate();
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios).filter((p) => p.organization?.id === organization?.id);
  const teams = useGlobalStore((state) => state.teams);

  const { pageTemplates, query: pageTemplatesQuery } = usePageTemplates(organization?.id || '', portfolio?.id);
  const pageTemplate = useMemo(() => {
    return pageTemplates.find((template) => template.id === templateId);
  }, [pageTemplates, templateId]);

  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  const [objectKeyMappings, setObjectKeyMappings] = useState<Record<string, string>>({});
  const [formMappings, setFormMappings] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [currentLayout, setCurrentLayout] = useState<Layout[]>([]);

  // Extract object keys from the template
  const objectKeys = useMemo(() => {
    if (!pageTemplate) return [];
    return extractTemplateObjectKeys(pageTemplate);
  }, [pageTemplate]);

  // Reset mappings when template changes
  useEffect(() => {
    if (pageTemplate) {
      const emptyMappings = {};
      setObjectKeyMappings(emptyMappings);
      setFormMappings(emptyMappings);
    }
  }, [pageTemplate]);

  // Get detailed info about each object key including which components use it
  const objectKeyInfos = useMemo((): ObjectKeyInfo[] => {
    if (!pageTemplate?.config?.components) return [];

    const keyInfoMap = new Map<string, ObjectKeyInfo>();

    pageTemplate.config.components.forEach((component) => {
      component.component_data?.forEach((data) => {
        data.object_ids.forEach((objectKey) => {
          if (!keyInfoMap.has(objectKey)) {
            keyInfoMap.set(objectKey, {
              key: objectKey,
              objectType: data.object_type as ObjectType,
              usedInComponents: [],
            });
          }
          const info = keyInfoMap.get(objectKey)!;
          if (!info.usedInComponents.includes(component.name)) {
            info.usedInComponents.push(component.name);
          }
        });

        // Also check color_map keys
        if (data.color_map) {
          Object.keys(data.color_map).forEach((colorKey) => {
            if (!keyInfoMap.has(colorKey)) {
              keyInfoMap.set(colorKey, {
                key: colorKey,
                objectType: data.object_type as ObjectType,
                usedInComponents: [],
              });
            }
            const info = keyInfoMap.get(colorKey)!;
            if (!info.usedInComponents.includes(component.name)) {
              info.usedInComponents.push(component.name);
            }
          });
        }
      });
    });

    return Array.from(keyInfoMap.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [pageTemplate]);

  // Get options for dropdowns based on object type
  const getOptionsByObjectType = (objectType: ObjectType) => {
    switch (objectType) {
      case ObjectType.Portfolio:
        return portfolios.map((p) => ({ label: p.name, value: p.id }));
      case ObjectType.Project:
        return teams.map((t) => ({ label: t.name, value: t.id }));
      case ObjectType.Board:
      case ObjectType.Sprint:
        return teams.flatMap((t) =>
          t.subprojects.map((board) => ({ label: `${t.name} - ${board.name}`, value: board.id })),
        );
      case ObjectType.Initiative:
        return initiatives?.map((initiative: Initiative) => ({
          value: initiative.id,
          label: initiative.name,
        }));
      default:
        return [];
    }
  };

  // Handle mapping changes
  const handleMappingChange = (objectKey: string, selectedUuid: string) => {
    setFormMappings((prev) => ({
      ...prev,
      [objectKey]: selectedUuid,
    }));
  };

  // Validate mappings and update errors
  useEffect(() => {
    if (!pageTemplate) return;
    const validation = validateTemplateObjectMappings(pageTemplate, formMappings);
    setValidationErrors(validation.missingKeys);
  }, [pageTemplate, formMappings]);

  // Handle apply button click
  const handleApply = () => {
    if (!pageTemplate) return;

    const validation = validateTemplateObjectMappings(pageTemplate, formMappings);

    if (!validation.isValid) {
      setValidationErrors(validation.missingKeys);
      return;
    }

    // Apply the form mappings to the actual mappings
    setObjectKeyMappings({ ...formMappings });

    const convertedComponents = convertPageTemplateToComponents(
      pageTemplate,
      formMappings,
      'template-preview', // Temporary page ID for preview
    );

    setComponents(convertedComponents);

    // Generate layouts for the components
    const layouts = convertedComponents.map((component, index) => {
      if (!component.id) return createDefaultLayout(`component-${index}`, index);

      if (component.layout_data && Object.keys(component.layout_data).length > 0) {
        return layoutDataToLayout(component.layout_data, component.id);
      }

      return createDefaultLayout(component.id, index);
    });

    setCurrentLayout(layouts);
  };

  // Check if there are changes from current applied state
  const hasChanges = useMemo(() => {
    const formKeys = Object.keys(formMappings);
    const currentKeys = Object.keys(objectKeyMappings);

    if (formKeys.length !== currentKeys.length) return true;

    return formKeys.some((key) => formMappings[key] !== objectKeyMappings[key]);
  }, [formMappings, objectKeyMappings]);

  const allMappingsComplete =
    objectKeys.length > 0 && validationErrors.length === 0 && objectKeys.every((key) => formMappings[key]);

  const shouldDisableApplyButton = !allMappingsComplete || !hasChanges;

  const header = (
    <Flex align="center" gap="md" mb="lg">
      <Group gap={8} style={{ marginTop: '1.5rem', marginLeft: '1rem' }}>
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="arrow_back" size={20} color={baseWhite} />}
          onClick={() => navigate('/application/dashboards')}
        >
          Back
        </Button>
        <Icon name="dashboard_customize" size={36} color={secondaryBase} />
        <H2>{pageTemplate?.name}</H2>
      </Group>
    </Flex>
  );

  const dashboardTemplateNotFound = Boolean(!pageTemplate && templateId && !pageTemplatesQuery.isLoading);

  return (
    <MenuDrivenContainer header={header}>
      {dashboardTemplateNotFound ? (
        <DashboardInfoCard
          title="Dashboard template not found"
          objectName={i18n.t('common.dashboard.template')}
          iconName="dashboard_customize"
        />
      ) : (
        <Stack>
          <Paper withBorder radius="md" p="md" ml="md" mr="md" mt="md">
            <Group justify="space-between" align="flex-start">
              <Text size="medium" weight="bold">
                {`${i18n.t('common.dashboard.template')} Mapping`}
              </Text>
              <Group align="flex-center" justify="end">
                <Group gap="sm">
                  {objectKeyInfos.map((keyInfo) => {
                    const options = getOptionsByObjectType(keyInfo.objectType);
                    const objectType = getTemplateObjectType(keyInfo.objectType);
                    return (
                      <Stack gap={5} key={keyInfo.key}>
                        <Tooltip
                          label={
                            <Stack gap={5}>
                              <Text size="regular" weight="medium">
                                {`${objectType} used in:`}
                              </Text>
                              {keyInfo.usedInComponents.map((component) => {
                                return (
                                  <Text size="small" key={component}>
                                    {component}
                                  </Text>
                                );
                              })}
                            </Stack>
                          }
                          variant="black"
                          multiline
                          style={{ maxWidth: 400 }}
                        >
                          <Group gap="xs">
                            <Text size="small" weight="medium">
                              {objectType}
                            </Text>
                            <Icon name="info" size={16} color={secondaryBase} />
                          </Group>
                        </Tooltip>
                        <Select
                          placeholder={`Select ${objectType}...`}
                          options={options}
                          value={formMappings[keyInfo.key] || null}
                          onChange={(value) => value && handleMappingChange(keyInfo.key, value)}
                          searchable
                          style={{ width: '250px' }}
                        />
                      </Stack>
                    );
                  })}
                </Group>
                <Divider orientation="vertical" />

                <Group gap="xs">
                  <Button
                    variant="primary"
                    size="sm"
                    radius="xl"
                    onClick={handleApply}
                    disabled={shouldDisableApplyButton}
                    style={{
                      border: shouldDisableApplyButton ? '1px solid var(--sky-dark)' : undefined,
                    }}
                    leftSection={
                      <Icon
                        name="check_circle_outline"
                        size={16}
                        color={!shouldDisableApplyButton ? baseWhite : skyDark}
                        style={{ cursor: shouldDisableApplyButton ? 'not-allowed' : 'pointer' }}
                      />
                    }
                  >
                    Apply
                  </Button>
                </Group>
              </Group>
            </Group>
          </Paper>

          {components.length === 0 ? (
            <DashboardInfoCard
              title="Object mappings incomplete"
              description="Please select all required objects in the mapping section above to see the dashboard template preview."
              objectName={i18n.t('common.dashboard.template')}
              iconName="dashboard_customize"
            />
          ) : (
            <ResponsiveGridLayout
              key={`grid`}
              className="layout"
              rowHeight={120}
              style={{ width: '100%' }}
              layouts={{ lg: currentLayout }}
              isDraggable={false}
              isResizable={false}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
              cols={{ lg: 12, md: 9, sm: 6, xs: 3 }}
              margin={[20, 40]}
            >
              {components.map((component, index) => {
                const componentLayout =
                  currentLayout.find((layout) => layout.i === component.id) ||
                  createDefaultLayout(`component-${index}`, index);

                return (
                  <div key={index} data-grid={componentLayout}>
                    <DashboardWidget component={component} isPreview={true} />
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          )}
        </Stack>
      )}
    </MenuDrivenContainer>
  );
};
